import React, { useState, useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    LabelList
} from "recharts";

// Paleta estendida para suportar "N" barras empilhadas
const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#0088fe", "#00c49f", "#ffbb28"];

// Componente de etiqueta inalterado (usado apenas se showDifferenceLabel = true)
const DifferenceLabel = ({ x, y, width, index, data }) => {
    const item = data?.[index];
    if (!item) return null;

    const arealizar = (item.AREALIZAR ?? 0) * -1;
    const diffText = `${arealizar < 0 ? '-' : ''} R$ ${Math.abs(arealizar).toLocaleString("pt-BR", {
        minimumFractionDigits: 2
    })}`;

    const adjustedY = arealizar < 0 ? y - 10 : y - 20;

    return (
        <text
            x={x + width / 2}
            y={adjustedY}
            textAnchor="middle"
            fill={arealizar < 0 ? "red" : "green"}
            fontSize={12}
        >
            {diffText}
        </text>
    );
};

// Nomes dos meses em português para exibição no header de paginação
const MONTH_NAMES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

/**
 * Extrai a chave "ANO-MES" de um item cujo campo de data (titleKey) está no
 * formato "DD/MM/YYYY". Retorna null se o valor não existir ou o formato for inválido.
 */
function extractMonthKey(item, titleKey) {
    const raw = item?.[titleKey];
    if (!raw) return null;
    const parts = raw.split("/");
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}`; // "YYYY-MM"
}

/**
 * A partir de uma chave "YYYY-MM" devolve o número do dia de um item no formato "DD/MM/YYYY".
 */
function extractDay(item, titleKey) {
    const raw = item?.[titleKey];
    if (!raw) return raw;
    return raw.split("/")[0]; // Apenas o dia, ex: "05"
}

const StackedBarChart = ({
    data,
    dataKeys,
    chartLabel,
    title,
    showDifferenceLabel = false,
    dataType = "currency",
    pageByMonth = false,   // <-- novo prop: ativa paginação por mês
}) => {

    const formatCurrency = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
    const formatNumber  = (value) => new Intl.NumberFormat("pt-BR").format(value);
    const formatValue   = (value) => dataType === "currency" ? formatCurrency(value) : formatNumber(value);

    // ── Lógica de paginação ────────────────────────────────────────────────────

    // Lista ordenada de meses presentes nos dados (ex: ["2025-03", "2025-04"])
    const monthKeys = useMemo(() => {
        if (!pageByMonth) return [];
        const seen = new Set();
        (data || []).forEach((item) => {
            const key = extractMonthKey(item, title);
            if (key) seen.add(key);
        });
        return Array.from(seen).sort();
    }, [data, title, pageByMonth]);

    const [currentMonthIdx, setCurrentMonthIdx] = useState(0);

    // Dados filtrados pelo mês atual + campo DATA substituído pelo número do dia
    const displayData = useMemo(() => {
        if (!pageByMonth || monthKeys.length === 0) return data || [];

        const activeKey = monthKeys[currentMonthIdx];
        return (data || [])
            .filter((item) => extractMonthKey(item, title) === activeKey)
            .map((item) => ({
                ...item,
                [title]: extractDay(item, title),   // substitui "05/04/2025" por "05"
            }));
    }, [data, title, pageByMonth, monthKeys, currentMonthIdx]);

    // Rótulo legível do mês atual: "Abril / 2025"
    const currentMonthLabel = useMemo(() => {
        if (monthKeys.length === 0) return "";
        const [year, month] = monthKeys[currentMonthIdx].split("-");
        return `${MONTH_NAMES[parseInt(month, 10) - 1]} / ${year}`;
    }, [monthKeys, currentMonthIdx]);

    // ── Tooltip ────────────────────────────────────────────────────────────────

    const customTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // No modo pageByMonth o label é só o dia; complementa com mês/ano
            const displayLabel = pageByMonth && monthKeys.length > 0
                ? `Dia ${label} — ${currentMonthLabel}`
                : label;

            return (
                <div
                    className="custom-tooltip shadow-sm rounded"
                    style={{ backgroundColor: "#fff", padding: "12px", border: "1px solid #ccc" }}
                >
                    <p className="fw-bold mb-2 pb-1 border-bottom">{displayLabel}</p>
                    {payload.map((entry, idx) => (
                        <div key={idx} className="d-flex justify-content-between mb-1">
                            <span style={{ color: entry.color, marginRight: "15px" }}>
                                {chartLabel[idx] || entry.name}:
                            </span>
                            <span className="fw-bold">{formatValue(entry.value)}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="rounded p-4 bg-white">

            {/* Controles de paginação — renderizados apenas quando pageByMonth está ativo */}
            {pageByMonth && monthKeys.length > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setCurrentMonthIdx((i) => Math.max(i - 1, 0))}
                        disabled={currentMonthIdx === 0}
                        title="Mês anterior"
                    >
                        &#8592;
                    </button>

                    <span className="fw-semibold" style={{ minWidth: "160px", textAlign: "center" }}>
                        {currentMonthLabel}
                    </span>

                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setCurrentMonthIdx((i) => Math.min(i + 1, monthKeys.length - 1))}
                        disabled={currentMonthIdx === monthKeys.length - 1}
                        title="Próximo mês"
                    >
                        &#8594;
                    </button>
                </div>
            )}

            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    data={displayData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey={title} tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} />
                    <YAxis tickFormatter={formatNumber} domain={["auto", "auto"]} tick={{ fontSize: 12 }} />
                    <Tooltip content={customTooltip} cursor={{ fill: "#f3f2f1" }} />
                    <Legend />

                    {dataKeys.map((key, index) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            stackId="a"
                            fill={COLORS[index % COLORS.length]}
                            name={chartLabel[index] || key}
                        >
                            {(showDifferenceLabel && index === 1) && (
                                <LabelList
                                    dataKey={key}
                                    content={(props) => <DifferenceLabel {...props} data={displayData} />}
                                />
                            )}
                        </Bar>
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default StackedBarChart;